import React, { useEffect, useState } from 'react';
import categoryService from '../services/categoryService';
import { useAuth } from '../context/AuthContext';

const normalizeList = (values = []) =>
  Array.isArray(values)
    ? values
        .filter((entry) => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter(Boolean)
    : [];

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: '', status: 'Active', subcategories: '' });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const canRead = user?.permissions?.includes('categories:read');
  const canCreate = user?.permissions?.includes('categories:create');
  const canUpdate = user?.permissions?.includes('categories:update');
  const canDelete = user?.permissions?.includes('categories:delete');

  const loadCategories = async () => {
    try {
      const data = await categoryService.fetchCategories();
      setCategories(data);
    } catch (err) {
      setError('Failed to load categories.');
    }
  };

  useEffect(() => {
    if (canRead) {
      loadCategories();
    }
  }, [canRead]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (editingId && !canUpdate) {
      setError('You do not have permission to update categories.');
      return;
    }
    if (!editingId && !canCreate) {
      setError('You do not have permission to create categories.');
      return;
    }
    try {
      const payload = {
        ...form,
        subcategories: form.subcategories,
      };
      if (editingId) {
        await categoryService.updateCategory(editingId, payload);
      } else {
        await categoryService.createCategory(payload);
      }
      setForm({ name: '', status: 'Active', subcategories: '' });
      setEditingId(null);
      loadCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save category.');
    }
  };

  const handleEdit = (category) => {
    if (!canUpdate) {
      setError('You do not have permission to update categories.');
      return;
    }
    setEditingId(category._id);
    setForm({
      name: category.name,
      status: category.status,
      subcategories: Array.isArray(category.subcategories)
        ? category.subcategories.join(', ')
        : '',
    });
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      setError('You do not have permission to delete categories.');
      return;
    }
    if (!window.confirm('Delete this category?')) return;
    try {
      await categoryService.deleteCategory(id);
      loadCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete category.');
    }
  };

  if (!canRead) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="mt-2">You do not have the required permissions to view categories.</p>
      </div>
    );
  }

  const handleQuickPickToggle = async (category) => {
    if (!canUpdate) {
      setError('You do not have permission to update categories.');
      return;
    }
    const nextValue = !category.quickPickEnabled;
    const previous = categories;
    setCategories((prev) =>
      prev.map((item) =>
        item._id === category._id
          ? { ...item, quickPickEnabled: nextValue }
          : item
      )
    );
    try {
      await categoryService.updateCategory(category._id, {
        name: category.name,
        status: category.status,
        quickPickEnabled: nextValue,
      });
      loadCategories();
    } catch (err) {
      setCategories(previous);
      setError(err.response?.data?.message || 'Unable to update quick pick setting.');
    }
  };

  const handleQuickPickSubcategoryToggle = async (category, subcategory) => {
    if (!canUpdate) {
      setError('You do not have permission to update categories.');
      return;
    }
    const targetKey = subcategory.trim().toLowerCase();
    const current = normalizeList(category.quickPickSubcategories);
    const nextValue = current.some((entry) => entry.trim().toLowerCase() === targetKey)
      ? current.filter((entry) => entry.trim().toLowerCase() !== targetKey)
      : [...current, subcategory];

    const previous = categories;
    setCategories((prev) =>
      prev.map((item) =>
        item._id === category._id
          ? { ...item, quickPickSubcategories: nextValue }
          : item
      )
    );

    try {
      await categoryService.updateCategory(category._id, {
        name: category.name,
        status: category.status,
        quickPickSubcategories: nextValue,
      });
      loadCategories();
    } catch (err) {
      setCategories(previous);
      setError(err.response?.data?.message || 'Unable to update subcategory quick pick setting.');
    }
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto lg:overflow-hidden bg-slate-50 px-6 py-6 flex flex-col gap-4">
      <div className="bg-white/90 backdrop-blur rounded-2xl border border-slate-200 px-5 py-4 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Product Categories</h1>
            <p className="text-sm text-slate-500">
              Organize products with reusable categories and subcategories.
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {categories.length} total
            </span>
          </div>
        </div>
        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden grid grid-cols-1 gap-6 xl:grid-cols-12">
        {(canCreate || canUpdate) && (
          <div className="xl:col-span-4 xl:sticky xl:top-6 self-stretch h-full min-h-0 overflow-y-auto">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {editingId ? 'Edit category' : 'Add category'}
                  </h2>
                  <p className="text-xs text-slate-500">
                    Keep names unique and concise for faster checkout.
                  </p>
                </div>
                {editingId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setForm({ name: '', status: 'Active', subcategories: '' });
                    }}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                  >
                    Cancel edit
                  </button>
                )}
              </div>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Category name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                    placeholder="e.g. Party Supplies"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Subcategories</label>
                  <textarea
                    rows="3"
                    value={form.subcategories}
                    onChange={(e) => setForm((prev) => ({ ...prev, subcategories: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                    placeholder="e.g. Soft Drinks, Energy, Juice"
                  />
                  <p className="mt-1 text-xs text-slate-500">Comma-separated, unique within this category.</p>
                </div>
                <button
                  type="submit"
                  className="w-full rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark"
                >
                  {editingId ? 'Save changes' : 'Add category'}
                </button>
              </form>
            </div>
          </div>
        )}

        <div className={`${canCreate || canUpdate ? 'xl:col-span-8' : 'xl:col-span-12'} min-h-0`}>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm h-full flex flex-col min-h-0">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Categories list</h2>
                <p className="text-xs text-slate-500">Scroll within the list to review all categories.</p>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Name</th>
                    <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Subcategories</th>
                    <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Quick Pick</th>
                    <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {categories.map((category) => (
                    <tr key={category._id} className="hover:bg-slate-50/70">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{category.name}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            category.status === 'Active'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {category.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {Array.isArray(category.subcategories) && category.subcategories.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {normalizeList(category.subcategories).map((subcategory) => {
                              const enabled = normalizeList(category.quickPickSubcategories).some(
                                (entry) => entry.trim().toLowerCase() === subcategory.trim().toLowerCase()
                              );
                              return (
                                <button
                                  key={`${category._id}-${subcategory}`}
                                  type="button"
                                  onClick={() => handleQuickPickSubcategoryToggle(category, subcategory)}
                                  disabled={!canUpdate}
                                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition ${
                                    enabled
                                      ? 'border-primary bg-primary/10 text-primary'
                                      : 'border-slate-200 text-slate-600 hover:border-primary/40'
                                  } ${canUpdate ? '' : 'opacity-60 cursor-not-allowed'}`}
                                  title={
                                    enabled
                                      ? 'Disable quick pick for this subcategory'
                                      : 'Enable quick pick for this subcategory'
                                  }
                                >
                                  <span>{subcategory}</span>
                                  <span className="text-[10px] uppercase tracking-wide">
                                    {enabled ? 'Quick Pick' : 'Off'}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <label className={`inline-flex items-center gap-3 text-sm text-slate-600 ${canUpdate ? '' : 'opacity-60 cursor-not-allowed'}`}>
                          <div className="relative inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={Boolean(category.quickPickEnabled)}
                              onChange={() => handleQuickPickToggle(category)}
                              disabled={!canUpdate}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-primary transition-colors" />
                            <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                          </div>
                          {category.quickPickEnabled ? 'On' : 'Off'}
                        </label>
                      </td>
                      <td className="px-4 py-3 text-right space-x-3 text-sm font-medium">
                        {canUpdate && (
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-slate-600 hover:text-slate-900"
                          >
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(category._id)}
                            className="text-rose-600 hover:text-rose-700"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
